import type {
  CandidateScore,
  CandidateStrength,
  ProposedEstateCandidate,
  ProposedEstateSearchFilters,
  ProposedParcel,
  ReferenceType,
} from '@types';
import type { BoundingBox, Point } from '@types';
import type { EstateJsonData } from '@types';

const VIC_PROPOSED_WFS_URL = 'https://opendata.maps.vic.gov.au/geoserver/wfs';
const VIC_PROPOSED_LAYER = 'open-data-platform:v_s_parcel_proposed';
const VIC_APPROVED_LAYER = 'open-data-platform:v_s_parcel_approved';
const VIC_LOCALITY_LAYER = 'open-data-platform:vmlite_locality_polygon';
const VIC_POSTCODE_LAYER = 'open-data-platform:vmlite_postcode_polygon';
const DATASET_NAME = 'Vicmap Property Simplified Parcel Approved and Proposed';
const DEFAULT_PROPOSED_PAGE_SIZE = 500;
const DEFAULT_APPROVED_PAGE_SIZE = 2000;

type GeoJsonPosition = [number, number] | [number, number, number];

interface GeoJsonFeature {
  id?: string | number;
  properties?: Record<string, unknown>;
  bbox?: [number, number, number, number];
  geometry?: {
    type: 'Polygon' | 'MultiPolygon';
    coordinates: GeoJsonPosition[][] | GeoJsonPosition[][][];
  } | null;
}

interface GeoJsonFeatureCollection {
  features?: GeoJsonFeature[];
}

const FIELD_CANDIDATES = {
  sourceId: ['parcel_pfi', 'pfi', 'parcelpfi', 'ufi', 'objectid', 'fid'],
  planNumber: ['parcel_plan_number', 'plannumber', 'plan_number', 'plan_no', 'spi_plan'],
  lotNumber: ['parcel_lot_number', 'lotnumber', 'lot_number', 'lot_no', 'parcel_spi'],
  address: ['ezi_address', 'address', 'street_address'],
  suburb: ['locality_name', 'locality', 'suburb', 'town_name'],
  postcode: ['postcode', 'post_code'],
  areaSqm: ['parcel_area', 'area_sqm', 'shape_area', 'area'],
  road: ['road'],
  status: ['status'],
  furtherDescription: ['further_description'],
};

export async function searchVicProposedEstateCandidates(
  filters: ProposedEstateSearchFilters
): Promise<ProposedEstateCandidate[]> {
  const parcels = await fetchVicProposedParcels(filters);
  return groupProposedParcels(parcels)
    .map(candidate => ({
      ...candidate,
      suburb: candidate.suburb === 'Unknown suburb' && filters.suburb ? filters.suburb : candidate.suburb,
      postcode: candidate.postcode ?? filters.postcode,
      displayName:
        candidate.suburb === 'Unknown suburb' && filters.suburb
          ? `${filters.suburb} proposed land${candidate.parcels[0]?.planNumber ? ` (${candidate.parcels[0].planNumber})` : ''}`
          : candidate.displayName,
    }))
    .filter(candidate => candidate.parcels.length > 0);
}

export async function fetchVicProposedParcels(
  filters: ProposedEstateSearchFilters
): Promise<ProposedParcel[]> {
  const resolvedBounds = await resolveSearchBounds(filters);
  const [proposedParcels, approvedParcels] = await Promise.all([
    fetchVicParcelsForLayer(VIC_PROPOSED_LAYER, resolvedBounds, 'proposed'),
    fetchVicParcelsForLayer(VIC_APPROVED_LAYER, resolvedBounds, 'approved'),
  ]);

  return [...proposedParcels, ...approvedParcels];
}

async function fetchVicParcelsForLayer(
  layer: string,
  filters: ProposedEstateSearchFilters,
  layerStatus: 'proposed' | 'approved'
): Promise<ProposedParcel[]> {
  const params = new URLSearchParams({
    service: 'WFS',
    version: '2.0.0',
    request: 'GetFeature',
    typeNames: layer,
    outputFormat: 'application/json',
    count: String(layerStatus === 'approved' ? DEFAULT_APPROVED_PAGE_SIZE : DEFAULT_PROPOSED_PAGE_SIZE),
  });

  if (hasCompleteBounds(filters)) {
    params.set(
      'bbox',
      `${filters.west},${filters.south},${filters.east},${filters.north},EPSG:4326`
    );
  }

  const response = await fetch(`${VIC_PROPOSED_WFS_URL}?${params.toString()}`);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Vic proposed parcel search failed: ${response.status} ${errorText.substring(0, 160)}`
    );
  }

  const data = (await response.json()) as GeoJsonFeatureCollection;
  return (data.features ?? [])
    .map(feature => featureToParcel(feature, layerStatus))
    .filter((parcel): parcel is ProposedParcel => parcel !== null);
}

export async function resolveVicLocalityBounds(suburb: string): Promise<Required<Pick<
  ProposedEstateSearchFilters,
  'west' | 'south' | 'east' | 'north'
>>> {
  const params = new URLSearchParams({
    service: 'WFS',
    version: '2.0.0',
    request: 'GetFeature',
    typeNames: VIC_LOCALITY_LAYER,
    outputFormat: 'application/json',
    count: '1',
    cql_filter: `locality_name='${escapeCql(suburb.trim().toUpperCase())}' AND state='VIC'`,
  });

  const response = await fetch(`${VIC_PROPOSED_WFS_URL}?${params.toString()}`);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Vic locality lookup failed: ${response.status} ${errorText.substring(0, 160)}`
    );
  }

  const data = (await response.json()) as GeoJsonFeatureCollection;
  const bbox = data.features?.[0]?.bbox;
  if (!bbox) {
    throw new Error(`No VIC locality boundary found for "${suburb}". Try map bounds instead.`);
  }

  return {
    west: bbox[0],
    south: bbox[1],
    east: bbox[2],
    north: bbox[3],
  };
}

export async function resolveVicPostcodeBounds(postcode: string): Promise<Required<Pick<
  ProposedEstateSearchFilters,
  'west' | 'south' | 'east' | 'north'
>>> {
  const params = new URLSearchParams({
    service: 'WFS',
    version: '2.0.0',
    request: 'GetFeature',
    typeNames: VIC_POSTCODE_LAYER,
    outputFormat: 'application/json',
    count: '1',
    cql_filter: `postcode='${escapeCql(postcode.trim())}' AND state='VIC'`,
  });

  const response = await fetch(`${VIC_PROPOSED_WFS_URL}?${params.toString()}`);
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Vic postcode lookup failed: ${response.status} ${errorText.substring(0, 160)}`
    );
  }

  const data = (await response.json()) as GeoJsonFeatureCollection;
  const bbox = data.features?.[0]?.bbox;
  if (!bbox) {
    throw new Error(`No VIC postcode boundary found for "${postcode}". Try map bounds instead.`);
  }

  return {
    west: bbox[0],
    south: bbox[1],
    east: bbox[2],
    north: bbox[3],
  };
}

export function groupProposedParcels(parcels: ProposedParcel[]): ProposedEstateCandidate[] {
  const groups = new Map<string, ProposedParcel[]>();
  const approvedContext = parcels.filter(parcel => parcel.layerStatus === 'approved');
  const ignoredContext = parcels.filter(parcel => parcel.reviewRole === 'ignored-context');

  for (const parcel of parcels.filter(parcel => parcel.reviewRole === 'sale-lot')) {
    const key =
      normalizeKey(parcel.planNumber) ||
      normalizeKey(parcel.suburb) ||
      gridKey(parcel.points[0] ?? { lat: 0, lng: 0 });
    const group = groups.get(key) ?? [];
    group.push(parcel);
    groups.set(key, group);
  }

  return Array.from(groups.entries())
    .map(([key, group]) => buildCandidate(key, group, approvedContext, ignoredContext))
    .sort((a, b) => b.score.score - a.score.score || b.parcels.length - a.parcels.length);
}

export function scoreProposedEstateCandidate(parcels: ProposedParcel[]): CandidateScore {
  let score = 0;
  const reasons: string[] = [];

  if (parcels.length >= 20) {
    score += 35;
    reasons.push(`${parcels.length} proposed lots is a strong subdivision signal`);
  } else if (parcels.length >= 8) {
    score += 24;
    reasons.push(`${parcels.length} proposed lots suggests a possible estate`);
  } else if (parcels.length >= 3) {
    score += 12;
    reasons.push(`${parcels.length} proposed lots is enough to review`);
  } else {
    reasons.push('Very few proposed lots; may be a boundary change or small subdivision');
  }

  const residentialSized = parcels.filter(parcel => {
    const area = getParcelArea(parcel);
    return area >= 180 && area <= 1200;
  }).length;
  const residentialRatio = parcels.length ? residentialSized / parcels.length : 0;

  if (residentialRatio >= 0.65) {
    score += 28;
    reasons.push('Most lots are residential-sized');
  } else if (residentialRatio >= 0.35) {
    score += 14;
    reasons.push('Some lots are residential-sized');
  } else {
    reasons.push('Lot sizes do not strongly resemble residential land release');
  }

  const bounds = computeBounds(parcels.flatMap(parcel => parcel.points));
  const widthKm = approximateLngKm(bounds.east - bounds.west, (bounds.north + bounds.south) / 2);
  const heightKm = approximateLatKm(bounds.north - bounds.south);
  const spreadKm = Math.max(widthKm, heightKm);

  if (spreadKm <= 1.2 && parcels.length >= 3) {
    score += 20;
    reasons.push('Lots are geographically clustered');
  } else if (spreadKm <= 2.5) {
    score += 10;
    reasons.push('Lots are within a reviewable local area');
  } else {
    reasons.push('Lots are spread out; grouping may need manual cleanup');
  }

  const hasPlanSignal = parcels.some(parcel => Boolean(parcel.planNumber));
  if (hasPlanSignal) {
    score += 10;
    reasons.push('Plan/source identifiers are available');
  }

  const ignoredCount = parcels.filter(parcel => parcel.reviewRole === 'ignored-context').length;
  if (ignoredCount > 0) {
    reasons.push(`${ignoredCount} context parcel(s) excluded from sale-lot count`);
  }

  const hasSuburbSignal = parcels.some(parcel => Boolean(parcel.suburb || parcel.postcode));
  if (hasSuburbSignal) {
    score += 7;
    reasons.push('Location metadata is available for review');
  }

  return {
    score: Math.min(score, 100),
    strength: scoreToStrength(score),
    reasons,
  };
}

export function candidateToEstateJson(candidate: ProposedEstateCandidate): EstateJsonData {
  const exportLots = [
    ...candidate.parcels.map((parcel, index) => ({ parcel, index, sourceRole: 'proposed-sale-lot' })),
    ...(candidate.approvedContextParcels ?? []).map((parcel, index) => ({
      parcel,
      index,
      sourceRole: 'approved-context-lot',
    })),
  ];

  const references = (candidate.ignoredParcels ?? [])
    .filter(parcel => parcel.layerStatus === 'proposed' && parcel.referenceType)
    .map((parcel, index) => ({
      name: referenceNameForParcel(parcel, index),
      type: parcel.referenceType ?? 'future',
      handleId: generateHandleId(`${candidate.id}-ref`, index),
      sourcePlanNumber: parcel.planNumber,
      sourceLayerStatus: parcel.layerStatus,
      sourceRole: parcel.reviewRole,
      sourceId: parcel.sourceId || parcel.id,
      points: parcel.points,
    }));

  return {
    geoLocation: candidate.center,
    name: candidate.displayName,
    stage: 'Proposed',
    address: candidate.address || [candidate.suburb, candidate.state, candidate.postcode]
      .filter(Boolean)
      .join(' '),
    website: '',
    status: 'draft',
    lots: exportLots.map(({ parcel, index, sourceRole }) => ({
      code: parcel.lotNumber || String(index + 1),
      handleId: generateHandleId(`${candidate.id}-${sourceRole}`, index),
      sourcePlanNumber: parcel.planNumber,
      sourceLayerStatus: parcel.layerStatus,
      sourceRole,
      sourceId: parcel.sourceId || parcel.id,
      landSize: Math.round(getParcelArea(parcel)),
      frontage: 0,
      price: 0,
      status: 'available',
      points: parcel.points,
    })),
    references,
  };
}

function buildCandidate(
  key: string,
  parcels: ProposedParcel[],
  approvedContext: ProposedParcel[],
  ignoredContext: ProposedParcel[]
): ProposedEstateCandidate {
  const planKey = normalizeKey(parcels[0]?.planNumber);
  const boundsForMatch = computeBounds(parcels.flatMap(parcel => parcel.points));
  const matchedIgnored = ignoredContext.filter(parcel =>
    isRelatedContextParcel(parcel, planKey, boundsForMatch, 0.0025)
  );
  const candidateFootprintBounds = computeBounds(
    [...parcels, ...matchedIgnored].flatMap(parcel => parcel.points)
  );
  const approvedSeeds = approvedContext.filter(parcel =>
    isRelatedContextParcel(parcel, planKey, candidateFootprintBounds, 0.006)
  );
  const relatedApprovedPlanKeys = approvedPlanKeysToInclude(approvedSeeds);
  const matchedApproved = approvedContext.filter(parcel => {
    const parcelPlanKey = normalizeKey(parcel.planNumber);
    return (
      approvedSeeds.includes(parcel) ||
      Boolean(parcelPlanKey && relatedApprovedPlanKeys.has(parcelPlanKey))
    );
  });
  const points = [...parcels, ...matchedApproved, ...matchedIgnored].flatMap(parcel => parcel.points);
  const bounds = computeBounds(points);
  const center = centerOfBounds(bounds);
  const primary = parcels[0];
  const score = scoreProposedEstateCandidate(parcels);
  if (matchedApproved.length) {
    score.reasons.push(`${matchedApproved.length} approved/titled parcel(s) shown as context`);
  }
  if (matchedIgnored.length) {
    score.reasons.push(`${matchedIgnored.length} proposed context parcel(s) excluded from sale-lot export`);
  }
  const plan = primary.planNumber || key.toUpperCase();
  const suburb = primary.suburb || 'Unknown suburb';

  return {
    id: `vic-proposed-${normalizeKey(plan || key) || Math.abs(center.lat + center.lng)}`,
    displayName: `${suburb} proposed land${primary.planNumber ? ` (${primary.planNumber})` : ''}`,
    suburb,
    state: 'VIC',
    postcode: primary.postcode,
    address: primary.address,
    center,
    bounds,
    parcels,
    approvedContextParcels: matchedApproved,
    ignoredParcels: matchedIgnored,
    score,
    source: {
      dataset: DATASET_NAME,
      service: VIC_PROPOSED_WFS_URL,
      importedAt: new Date().toISOString(),
    },
  };
}

function featureToParcel(feature: GeoJsonFeature, layerStatus: 'proposed' | 'approved'): ProposedParcel | null {
  if (!feature.geometry) return null;
  const points = geometryToOuterRing(feature.geometry);
  if (points.length < 3) return null;

  const properties = feature.properties ?? {};
  const sourceId = getStringProperty(properties, FIELD_CANDIDATES.sourceId) || String(feature.id ?? '');
  const planNumber = getStringProperty(properties, FIELD_CANDIDATES.planNumber);
  const lotNumber = getStringProperty(properties, FIELD_CANDIDATES.lotNumber);
  const areaSqm = getNumberProperty(properties, FIELD_CANDIDATES.areaSqm);
  const classification = classifyParcel(properties, layerStatus, lotNumber, areaSqm, points);

  return {
    id: String(feature.id ?? sourceId ?? `${points[0].lat}-${points[0].lng}`),
    sourceId,
    layerStatus,
    reviewRole: classification.role,
    referenceType: classification.referenceType,
    ignoreReason: classification.reason,
    planNumber,
    lotNumber,
    address: getStringProperty(properties, FIELD_CANDIDATES.address),
    suburb: getStringProperty(properties, FIELD_CANDIDATES.suburb),
    postcode: getStringProperty(properties, FIELD_CANDIDATES.postcode),
    areaSqm,
    points,
    properties,
  };
}

function classifyParcel(
  properties: Record<string, unknown>,
  layerStatus: 'proposed' | 'approved',
  lotNumber: string | undefined,
  areaSqm: number | undefined,
  points: Point[]
): {
  role: 'sale-lot' | 'approved-context' | 'ignored-context';
  referenceType?: ReferenceType;
  reason?: string;
} {
  if (layerStatus === 'approved') {
    return { role: 'approved-context' };
  }

  const road = getStringProperty(properties, FIELD_CANDIDATES.road);
  if (road?.toUpperCase() === 'Y') {
    return { role: 'ignored-context', referenceType: 'road', reason: 'Road parcel' };
  }

  const lot = (lotNumber ?? '').trim().toUpperCase();
  if (!lot) {
    return { role: 'ignored-context', referenceType: 'future', reason: 'Missing lot number' };
  }

  if (/^(ROAD|R\d*)$/i.test(lot)) {
    return { role: 'ignored-context', referenceType: 'road', reason: `Road lot marker: ${lot}` };
  }

  if (/^(RES|RESERVE|POS|OPEN)/i.test(lot)) {
    return { role: 'ignored-context', referenceType: 'reserve', reason: `Reserve lot marker: ${lot}` };
  }

  if (/^(DRAIN|WATER)/i.test(lot)) {
    return { role: 'ignored-context', referenceType: 'water', reason: `Water/drainage lot marker: ${lot}` };
  }

  if (/^(BAL|BALANCE|CP|COMMON|EASEMENT|A$|B$|C$)/i.test(lot)) {
    return { role: 'ignored-context', referenceType: 'future', reason: `Non-sale lot marker: ${lot}` };
  }

  const description = getStringProperty(properties, FIELD_CANDIDATES.furtherDescription);
  if (description && /ROAD/i.test(description)) {
    return { role: 'ignored-context', referenceType: 'road', reason: `Road parcel description: ${description}` };
  }

  if (description && /RESERVE|OPEN SPACE|PARK/i.test(description)) {
    return { role: 'ignored-context', referenceType: 'reserve', reason: `Reserve parcel description: ${description}` };
  }

  if (description && /DRAIN|WATER/i.test(description)) {
    return { role: 'ignored-context', referenceType: 'water', reason: `Water/drainage parcel description: ${description}` };
  }

  if (description && /EASEMENT|COMMON|BALANCE/i.test(description)) {
    return { role: 'ignored-context', referenceType: 'future', reason: `Non-sale parcel description: ${description}` };
  }

  if (!/^\d+[A-Z]?$/.test(lot)) {
    return { role: 'ignored-context', referenceType: 'future', reason: `Non-residential lot label: ${lot}` };
  }

  const area = areaSqm && areaSqm > 0 ? areaSqm : estimatePolygonAreaSqm(points);
  if (area < 120 || area > 10000) {
    return { role: 'ignored-context', referenceType: 'future', reason: `Lot area ${Math.round(area)}sqm outside sale-lot range` };
  }

  return { role: 'sale-lot' };
}

function referenceNameForParcel(parcel: ProposedParcel, index: number): string {
  const type = parcel.referenceType ?? 'future';
  const lot = parcel.lotNumber ? ` ${parcel.lotNumber}` : '';
  const plan = parcel.planNumber ? ` (${parcel.planNumber})` : '';
  return `${type}${lot}${plan}`.trim() || `reference ${index + 1}`;
}

function isRelatedContextParcel(
  parcel: ProposedParcel,
  planKey: string,
  candidateBounds: BoundingBox,
  expandDegrees: number
): boolean {
  if (planKey && normalizeKey(parcel.planNumber) === planKey) {
    return true;
  }

  const center = centerOfBounds(computeBounds(parcel.points));
  const expanded = expandBounds(candidateBounds, expandDegrees);
  return (
    center.lat >= expanded.south &&
    center.lat <= expanded.north &&
    center.lng >= expanded.west &&
    center.lng <= expanded.east
  );
}

function expandBounds(bounds: BoundingBox, degrees: number): BoundingBox {
  return {
    north: bounds.north + degrees,
    south: bounds.south - degrees,
    east: bounds.east + degrees,
    west: bounds.west - degrees,
  };
}

function approvedPlanKeysToInclude(parcels: ProposedParcel[]): Set<string> {
  const counts = new Map<string, number>();
  for (const parcel of parcels) {
    const key = normalizeKey(parcel.planNumber);
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return new Set(
    Array.from(counts.entries())
      .filter(([, count]) => count >= 2)
      .map(([key]) => key)
  );
}

function geometryToOuterRing(geometry: NonNullable<GeoJsonFeature['geometry']>): Point[] {
  const ring =
    geometry.type === 'Polygon'
      ? (geometry.coordinates as GeoJsonPosition[][])[0]
      : (geometry.coordinates as GeoJsonPosition[][][])[0]?.[0];

  if (!ring) return [];
  return ring.map(([lng, lat]) => ({ lat, lng }));
}

function getStringProperty(properties: Record<string, unknown>, names: string[]): string | undefined {
  for (const name of names) {
    const key = Object.keys(properties).find(k => k.toLowerCase() === name.toLowerCase());
    const value = key ? properties[key] : undefined;
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return undefined;
}

function getNumberProperty(properties: Record<string, unknown>, names: string[]): number | undefined {
  const value = getStringProperty(properties, names);
  if (!value) return undefined;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : undefined;
}

function getParcelArea(parcel: ProposedParcel): number {
  if (parcel.areaSqm && parcel.areaSqm > 0) return parcel.areaSqm;
  return estimatePolygonAreaSqm(parcel.points);
}

function estimatePolygonAreaSqm(points: Point[]): number {
  if (points.length < 3) return 0;
  const lat = points.reduce((sum, point) => sum + point.lat, 0) / points.length;
  const metersPerLng = 111_320 * Math.cos((lat * Math.PI) / 180);
  const metersPerLat = 110_574;
  const projected = points.map(point => ({
    x: point.lng * metersPerLng,
    y: point.lat * metersPerLat,
  }));

  let area = 0;
  for (let i = 0; i < projected.length; i++) {
    const current = projected[i];
    const next = projected[(i + 1) % projected.length];
    area += current.x * next.y - next.x * current.y;
  }
  return Math.abs(area / 2);
}

function computeBounds(points: Point[]): BoundingBox {
  return points.reduce(
    (bounds, point) => ({
      north: Math.max(bounds.north, point.lat),
      south: Math.min(bounds.south, point.lat),
      east: Math.max(bounds.east, point.lng),
      west: Math.min(bounds.west, point.lng),
    }),
    { north: -Infinity, south: Infinity, east: -Infinity, west: Infinity }
  );
}

function centerOfBounds(bounds: BoundingBox): Point {
  return {
    lat: (bounds.north + bounds.south) / 2,
    lng: (bounds.east + bounds.west) / 2,
  };
}

function scoreToStrength(score: number): CandidateStrength {
  if (score >= 70) return 'strong';
  if (score >= 42) return 'medium';
  return 'weak';
}

function hasCompleteBounds(filters: ProposedEstateSearchFilters): boolean {
  return [filters.west, filters.south, filters.east, filters.north].every(
    value => typeof value === 'number' && Number.isFinite(value)
  );
}

async function resolveSearchBounds(
  filters: ProposedEstateSearchFilters
): Promise<ProposedEstateSearchFilters> {
  if (hasCompleteBounds(filters)) {
    return filters;
  }

  if (filters.suburb?.trim()) {
    return resolveVicLocalityBounds(filters.suburb);
  }

  if (filters.postcode?.trim()) {
    return resolveVicPostcodeBounds(filters.postcode);
  }

  throw new Error('Enter a suburb, postcode, or complete map bounds before searching.');
}

function normalizeKey(value?: string): string {
  return (value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function escapeCql(value: string): string {
  return value.replace(/'/g, "''");
}

function gridKey(point: Point): string {
  return `${Math.round(point.lat * 100)}:${Math.round(point.lng * 100)}`;
}

function approximateLatKm(deltaLat: number): number {
  return Math.abs(deltaLat) * 110.574;
}

function approximateLngKm(deltaLng: number, lat: number): number {
  return Math.abs(deltaLng) * 111.32 * Math.cos((lat * Math.PI) / 180);
}

function generateHandleId(candidateId: string, index: number): string {
  const seed = `${candidateId}-${index}`.replace(/[^a-z0-9]/gi, '').toUpperCase();
  return (seed || 'PROPOSED').slice(0, 5).padEnd(5, String(index % 10));
}
